import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { safeStorage } from '../lib/storage'

import { apiBaseUrl } from '../lib/runtime'
const API_URL = apiBaseUrl()

/** Decode a JWT payload without verifying the signature (used only to check expiry client-side). */
function decodeJWTPayload(token: string): { exp?: number; sub?: string; name?: string; role?: string; venueId?: string } | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(atob(base64))
  } catch {
    return null
  }
}

/** Returns true if the token is missing, malformed, or its exp claim is in the past. */
function isTokenExpired(token: string): boolean {
  const payload = decodeJWTPayload(token)
  if (!payload?.exp) return true
  return payload.exp < Math.floor(Date.now() / 1000)
}

/** Returns how many seconds remain until the token expires. Returns 0 if expired/invalid. */
function tokenSecondsRemaining(token: string): number {
  const payload = decodeJWTPayload(token)
  if (!payload?.exp) return 0
  return Math.max(0, payload.exp - Math.floor(Date.now() / 1000))
}

/** A venue a manager can sign in to, as Hospora names it. */
export interface VenueChoice {
  id: string
  name: string
}

/**
 * Hospora sign-in needs to know which venue: the person is assigned to several and a session covers one.
 * The launch token has not been used up, so the same token is sent again with the answer.
 */
export class VenueChoiceRequired extends Error {
  constructor(readonly choose: VenueChoice[]) {
    super('Choose the venue to sign in to.')
    this.name = 'VenueChoiceRequired'
  }
}

const ADMIN_VENUE_KEY = 'jb_admin_venue'

/** How often (ms) to check whether the token needs refreshing. */
const REFRESH_CHECK_INTERVAL_MS = 10 * 60 * 1000   // 10 minutes
/** Refresh the token if fewer than this many seconds remain. */
const REFRESH_THRESHOLD_SECS    = 30 * 60            // 30 minutes

export const useAuthStore = defineStore('auth', () => {
  // Validate the stored token on startup – clear it immediately if it has expired.
  const storedToken = safeStorage.getItem('jb_staff_token')
  const validToken = storedToken && !isTokenExpired(storedToken) ? storedToken : null
  if (storedToken && !validToken) {
    safeStorage.removeItem('jb_staff_token')
    safeStorage.removeItem('jb_staff_user')
  }

  const token = ref<string | null>(validToken)
  const user = ref<{ email: string; name: string; role: string; venueId?: string } | null>(
    validToken ? JSON.parse(safeStorage.getItem('jb_staff_user') || 'null') : null
  )
  const loading = ref(false)
  const error = ref<string | null>(null)

  /**
   * The venue an admin has chosen to "view as".
   * Persisted to localStorage so it survives page refreshes.
   * Nothing until a venue is chosen or the venue list arrives (see ensureAdminVenue: the first venue).
   */
  const adminSelectedVenueId = ref<string>(safeStorage.getItem(ADMIN_VENUE_KEY) || '')

  function setAdminVenue(id: string) {
    adminSelectedVenueId.value = id
    safeStorage.setItem(ADMIN_VENUE_KEY, id)
  }

  /**
   * An admin's "view as" venue must be one of the organisation's venues: keep it if it is, else the first
   * (or none when there are no venues). Called once the venue list is known.
   */
  function ensureAdminVenue(venueIds: string[]) {
    if (venueIds.includes(adminSelectedVenueId.value)) return
    if (venueIds.length > 0) setAdminVenue(venueIds[0]!)
    else {
      adminSelectedVenueId.value = ''
      safeStorage.removeItem(ADMIN_VENUE_KEY)
    }
  }

  const isAuthenticated = computed(() => !!token.value)
  const isAdmin = computed(() => user.value?.role === 'admin')
  /**
   * The effective venue scope for this session:
   *  - Admins:        the venue they have selected in the sidebar
   *  - Venue staff:   the venue bound to their account (immutable)
   *  - null:          no scoping (should not happen after login)
   */
  const venueId = computed(() => {
    if (!user.value) return null
    if (user.value.role === 'admin') return adminSelectedVenueId.value || null
    return user.value.venueId ?? null
  })

  function getAuthHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token.value}`,
    }
  }

  /** Call after any API response. If the server returns 401, log out and redirect to login. */
  function handleUnauthorized() {
    logout()
    // Use dynamic import to avoid a circular dependency (router imports useAuthStore)
    import('../router').then(({ default: router }) => {
      router.push({ name: 'login' })
    })
  }

  async function login(email: string, password: string) {
    return startSession('/auth/login', { email, password })
  }

  /** Exchange a Hospora launch token (from #hospora_token=…) for a staff session. */
  async function loginWithHospora(hosporaToken: string, venueId?: string) {
    return startSession('/auth/hospora', { token: hosporaToken, ...(venueId ? { venueId } : {}) })
  }

  async function startSession(path: string, payload: Record<string, string>) {
    loading.value = true
    error.value = null
    try {
      const res = await fetch(`${API_URL}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        const data = await res.json()
        if (res.status === 409 && Array.isArray(data.choose)) {
          const choose = (data.choose as unknown[]).filter(
            (v): v is VenueChoice => !!v && typeof (v as VenueChoice).id === 'string' && typeof (v as VenueChoice).name === 'string',
          )
          // A question, not a failure: no error banner.
          if (choose.length) throw new VenueChoiceRequired(choose)
        }
        const failure = new Error(data.error || 'Login failed') as Error & { status?: number }
        failure.status = res.status
        throw failure
      }
      const data = await res.json()
      token.value = data.token
      user.value = data.user
      safeStorage.setItem('jb_staff_token', data.token)
      safeStorage.setItem('jb_staff_user', JSON.stringify(data.user))
      scheduleRefreshCheck()
    } catch (e: any) {
      if (!(e instanceof VenueChoiceRequired)) error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  function logout() {
    stopRefreshCheck()
    token.value = null
    user.value = null
    safeStorage.removeItem('jb_staff_token')
    safeStorage.removeItem('jb_staff_user')
  }

  // ── Token refresh machinery ────────────────────────────────────────────────

  let refreshTimer: ReturnType<typeof setInterval> | null = null

  /** Attempt to silently refresh the token using the /auth/refresh endpoint. */
  async function refreshToken(): Promise<void> {
    if (!token.value) return

    // If already expired, log out immediately rather than trying to refresh.
    if (isTokenExpired(token.value)) {
      handleUnauthorized()
      return
    }

    // Only refresh if expiry is within the threshold window.
    if (tokenSecondsRemaining(token.value) > REFRESH_THRESHOLD_SECS) return

    try {
      const res = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: getAuthHeaders(),
      })

      if (res.status === 401) {
        // Token was rejected (expired between check and request) – log out.
        handleUnauthorized()
        return
      }

      if (!res.ok) return // Non-auth error – leave the existing token in place

      const data = await res.json()
      if (data.token) {
        token.value = data.token
        safeStorage.setItem('jb_staff_token', data.token)
        // user info is embedded in the JWT; no need to re-fetch it
      }
    } catch {
      // Network error – don't log out, the token may still be valid
    }
  }

  function scheduleRefreshCheck() {
    stopRefreshCheck()
    // Run once immediately to handle the case where the tab is restored with
    // a token that is already near expiry.
    refreshToken()
    refreshTimer = setInterval(refreshToken, REFRESH_CHECK_INTERVAL_MS)
  }

  function stopRefreshCheck() {
    if (refreshTimer !== null) {
      clearInterval(refreshTimer)
      refreshTimer = null
    }
  }

  // If we loaded a valid token from localStorage, start the refresh loop now
  // so a tab that was left open overnight doesn't silently go stale.
  if (validToken) {
    scheduleRefreshCheck()
  }

  return {
    token, user, loading, error,
    isAuthenticated, isAdmin, venueId,
    adminSelectedVenueId, setAdminVenue, ensureAdminVenue,
    login, loginWithHospora, logout, getAuthHeaders, handleUnauthorized,
  }
})
